import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Calendar, TrendingUp, Video, Eye, ArrowLeft, Share2, Play, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import toast from 'react-hot-toast';
import VideoPreviewModal from '../components/VideoPreviewModal';
import { getPlayableVideoUrl } from '../utils/proxyMedia';

const WeeklyTrendDigest = () => {
  const { user, isAdmin } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [digest, setDigest] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [videoPreview, setVideoPreview] = useState(null);
  const [failedThumbIds, setFailedThumbIds] = useState(() => new Set());

  useEffect(() => {
    fetchDigest();
  }, [dateFrom, dateTo]);

  const fetchDigest = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/trend-spotter/weekly-digest?from=${dateFrom}&to=${dateTo}`);
      const result = await response.json();
      if (result.success) {
        setDigest(result);
      } else {
        toast.error(result.error || 'Failed to load digest');
      }
    } catch (error) {
      console.error('Error fetching digest:', error);
      toast.error('Failed to load digest');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const handleShareTrend = (trend) => {
    if (trend.status !== 'approved') {
      toast.error('Only approved trends can be shared');
      return;
    }
    const slug = (trend.trend_name || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'trend';
    const url = `${window.location.origin}/share/trend/${trend.id}-${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Share link copied! Anyone with the link can view this trend.');
    }).catch(() => toast.error('Failed to copy link'));
  };

  const getVideoUrl = (video) => {
    if (video.video_url) return video.video_url;
    if (video.videoUrl) return video.videoUrl;
    if (video.apify_metadata) {
      try {
        const meta = typeof video.apify_metadata === 'string' ? JSON.parse(video.apify_metadata) : video.apify_metadata;
        return meta.videoUrl || meta.video_url || meta.mediaUrls?.[0] || meta.webVideoUrl || null;
      } catch (_) {}
    }
    return video.source_url || null;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500 mx-auto mb-4"></div>
          <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Loading weekly digest...</p>
        </div>
      </div>
    );
  }

  if (!digest) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'} flex items-center justify-center`}>
        <div className="text-center">
          <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>No digest data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-zinc-950' : 'bg-gray-50'} py-8 px-4 sm:px-6 lg:px-8`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border p-6 mb-6 print:border-0 print:shadow-none`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Weekly Trend Digest
              </h1>
              <p className={`${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                {formatDate(digest.summary.period_start)} - {formatDate(digest.summary.period_end)}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <label className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>From:</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <div className="flex items-center gap-2">
                <label className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>To:</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className={`px-3 py-2 border rounded-lg ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <button
                onClick={() => navigate('/trend-spotter')}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isDark ? 'bg-zinc-800 hover:bg-zinc-700 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'}`}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </button>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            <div className={`${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className={`h-5 w-5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Total Trends</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{digest.summary.total_trends}</p>
            </div>
            <div className={`${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Video className={`h-5 w-5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Total Videos</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatNumber(digest.summary.total_videos)}</p>
            </div>
            <div className={`${isDark ? 'bg-zinc-800' : 'bg-gray-50'} rounded-lg p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Eye className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                <span className={`text-sm font-medium ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>Total Views</span>
              </div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatNumber(digest.summary.total_views)}</p>
            </div>
          </div>
        </div>

        {/* Trends List */}
        <div className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'} rounded-2xl border p-6 print:border-0 print:shadow-none`}>
          <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            New Trends ({digest.trends.length})
          </h2>
          
          {digest.trends.length === 0 ? (
            <div className={`text-center py-12 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No trends created in the selected date range</p>
            </div>
          ) : (
            <div className="space-y-6">
              {digest.trends.map((trend) => (
                <div
                  key={trend.id}
                  className={`${isDark ? 'bg-zinc-800 border-zinc-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-6 hover:shadow-md transition-shadow`}
                >
                  {/* Trend Newsletter roundup: left-side card — trendElements, Name, trendSummary, Template, howToUse, suggestedCaptions */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1 min-w-0">
                      {Array.isArray(trend.trend_elements) && trend.trend_elements.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {trend.trend_elements.map((el, i) => (
                            <span key={i} className={`px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-cyan-500/20 text-cyan-300' : 'bg-cyan-100 text-cyan-800'}`}>{el}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {trend.trend_name}
                        </h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          trend.status === 'approved' 
                            ? 'bg-green-100 text-green-800' 
                            : trend.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {trend.status}
                        </span>
                        {trend.trend_type && (
                          <span className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-zinc-700 text-zinc-300' : 'bg-gray-200 text-gray-700'}`}>
                            {trend.trend_type}
                          </span>
                        )}
                      </div>
                      {trend.trend_summary && (
                        <p className={`text-sm mb-2 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>{trend.trend_summary}</p>
                      )}
                      {trend.template && (trend.template.visual || trend.template.text_overlay) && (
                        <p className={`text-sm mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Template:</span>{' '}
                          {[trend.template.visual, trend.template.text_overlay].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      {trend.how_to_use && (
                        <p className={`text-sm mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                          <span className="font-medium">How to use:</span> {trend.how_to_use}
                        </p>
                      )}
                      {Array.isArray(trend.suggested_captions) && trend.suggested_captions.length > 0 && (
                        <p className={`text-sm mb-2 ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                          <span className="font-medium">Suggested captions:</span>{' '}
                          {trend.suggested_captions.slice(0, 3).map((c, i) => `"${c}"`).join(' · ')}
                        </p>
                      )}
                      <div className={`flex items-center gap-4 text-sm ${isDark ? 'text-zinc-400' : 'text-gray-600'}`}>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(trend.created_at)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Video className="h-4 w-4" />
                          {trend.saved_count} videos
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {formatNumber(trend.total_views)} views
                        </div>
                        {trend.avg_views > 0 && (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4" />
                            Avg: {formatNumber(trend.avg_views)}
                          </div>
                        )}
                      </div>
                    </div>
                    {trend.status === 'approved' && (
                      <button
                        onClick={() => handleShareTrend(trend)}
                        className={`px-3 py-2 rounded-lg flex items-center gap-2 ${isDark ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-900'} transition-colors`}
                        title="Copy shareable link"
                      >
                        <Share2 className="h-4 w-4" />
                        <span className="text-sm">Share</span>
                      </button>
                    )}
                  </div>
                  
                  {/* Video Previews */}
                  {trend.top_videos && trend.top_videos.length > 0 && (
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                      <p className={`text-xs font-medium mb-3 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Top Videos <span className="font-normal opacity-80">(click to preview)</span></p>
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {trend.top_videos.map((video) => {
                          const videoUrl = getVideoUrl(video);
                          const thumbUrl = video.cover_url || video.coverUrl || null;
                          const showThumb = thumbUrl && !failedThumbIds.has(video.id);
                          const showFallback = !thumbUrl || failedThumbIds.has(video.id);
                          const openPreview = () => setVideoPreview({
                            videoUrl: videoUrl || video.source_url || '',
                            creator: video.author_username || 'Unknown',
                            platform: video.platform || 'tiktok',
                            views: video.views,
                            likes: video.likes,
                            video: video
                          });
                          return (
                            <div key={video.id} className="flex-shrink-0">
                              <button
                                type="button"
                                onClick={openPreview}
                                className={`relative w-24 h-32 rounded-lg overflow-hidden ${isDark ? 'bg-zinc-700' : 'bg-gray-100'} hover:ring-2 ring-cyan-500/50 transition-all cursor-pointer`}
                              >
                                {showThumb && (
                                  <img
                                    src={thumbUrl}
                                    alt=""
                                    className="w-full h-full object-cover absolute inset-0"
                                    onError={() => setFailedThumbIds((prev) => new Set(prev).add(video.id))}
                                  />
                                )}
                                {showFallback && (
                                  <div className="w-full h-full flex items-center justify-center absolute inset-0">
                                    {videoUrl ? (
                                      <video
                                        src={getPlayableVideoUrl(videoUrl)}
                                        className="w-full h-full object-cover"
                                        muted
                                        loop
                                        playsInline
                                        onMouseEnter={(e) => {
                                          const playPromise = e.target.play();
                                          if (playPromise !== undefined) playPromise.catch(() => {});
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.pause();
                                          e.target.currentTime = 0;
                                        }}
                                      />
                                    ) : (
                                      <span className="text-zinc-500">
                                        <Play className="h-6 w-6 mx-auto block" />
                                        <span className="text-[10px] block mt-1">View trend</span>
                                      </span>
                                    )}
                                  </div>
                                )}
                                <div className="absolute top-1 left-1 z-10">
                                  {video.platform === 'instagram' ? (
                                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[8px] px-1 py-0.5 rounded font-medium">IG</span>
                                  ) : (
                                    <span className="bg-black text-white text-[8px] px-1 py-0.5 rounded font-medium">TT</span>
                                  )}
                                </div>
                                <div className="absolute bottom-1 left-1 right-1 z-10">
                                  <div className="text-[9px] font-medium text-white bg-black/60 backdrop-blur-sm rounded px-1 py-0.5 truncate">
                                    {formatNumber(video.views)} views
                                  </div>
                                </div>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <div className={`mt-4 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                        {user ? (
                          <Link
                            to={`/trend-spotter?trendId=${trend.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${isDark ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40' : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/25'}`}
                          >
                            <Sparkles className="h-4 w-4" />
                            Create a viral video with this trend
                          </Link>
                        ) : (
                          <button
                            type="button"
                            onClick={() => navigate('/login', { state: { from: { pathname: '/trend-spotter', search: `?trendId=${trend.id}` } } })}
                            className={`inline-flex items-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all ${isDark ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40' : 'bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg shadow-cyan-500/25'}`}
                          >
                            <Sparkles className="h-4 w-4" />
                            Log in to create a viral video with this trend
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {trend.why_it_works && (
                    <div className={`mt-4 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                      <p className={`text-sm ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                        <span className="font-medium">Why it works:</span> {trend.why_it_works}
                      </p>
                    </div>
                  )}
                  
                  {trend.hook && typeof trend.hook === 'object' && (
                    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-zinc-700' : 'border-gray-200'}`}>
                      <p className={`text-xs font-medium mb-1 ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Hook:</p>
                      <div className={`text-sm space-y-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
                        {trend.hook.visual && <div>Visual: {trend.hook.visual}</div>}
                        {trend.hook.text_overlay && <div>Text: {trend.hook.text_overlay}</div>}
                        {trend.hook.sound && <div>Sound: {trend.hook.sound}</div>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Video Preview Modal */}
      <VideoPreviewModal
        videoPreview={videoPreview}
        onClose={() => setVideoPreview(null)}
        formatNumber={formatNumber}
        highlightTimestamps={(text) => text}
      />

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:border-0 { border: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
};

export default WeeklyTrendDigest;
